import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import security from "@/assets/images/breadcrumbs/app.webp";
import technology from "@/assets/images/marketing/omh.webp";
import webdesign from "@/assets/images/breadcrumbs/web-dev.webp";
import aiImage from "@/assets/images/breadcrumbs/chat-bot.webp";
import ecommerce from "@/assets/images/ecommerce/ecommerce-image.webp";
import crm from "@/assets/images/breadcrumbs/customer-relationship-management.webp";
import { Briefcase, ArrowUpRight, Users, ShoppingBag, Puzzle, Megaphone, Bot, Globe } from "lucide-react";

// Static data outside component — never recreated on renders
const projects = [
    { img: crm,        title: "CRM Services",             description: "Custom CRM systems that streamline customer management, automate workflows, and help businesses grow with smarter data.",             slug: "crm-development",       icon: Users       },
    { img: ecommerce,  title: "Custom E-commerce Sites",  description: "High-performing online stores designed to convert visitors into customers with seamless shopping and checkout experiences.",         slug: "ecommerce-development", icon: ShoppingBag },
    { img: security,   title: "App Integration",          description: "Connect your apps, tools, and platforms seamlessly to eliminate silos and create a unified digital workflow.",                      slug: "app-integration",       icon: Puzzle      },
    { img: technology, title: "Digital Marketing",        description: "Data-driven strategies that grow your brand, engage audiences, and turn online traffic into loyal customers.",                      slug: "digital-marketing",     icon: Megaphone   },
    { img: aiImage,    title: "Knowledgebase Chatbot",    description: "AI chatbots trained on your content to provide instant, accurate answers to customers anytime.",                                   slug: "knowledgebase-chatbot", icon: Bot         },
    { img: webdesign,  title: "Web Design & Development", description: "Modern, responsive websites built to combine strong design with performance and user experience.",                                 slug: "web-design",            icon: Globe       },
];

const AUTO_INTERVAL = 4000;
const PROJECTS_LEN  = projects.length; // stable constant, avoids .length in deps

export default function Projects() {
    const sectionRef = useRef<HTMLDivElement>(null);
    const listRef    = useRef<HTMLDivElement>(null);
    const itemRefs   = useRef<(HTMLButtonElement | null)[]>([]);
    const autoRef    = useRef<ReturnType<typeof setInterval> | null>(null);
    const startRef   = useRef<number>(Date.now());

    const [active, setActive]                   = useState(0);
    const [hovered, setHovered]                 = useState<number | null>(null);
    const [animating, setAnimating]             = useState(false);
    const [indicatorTop, setIndicatorTop]       = useState(0);
    const [indicatorHeight, setIndicatorHeight] = useState(0);
    const [progress, setProgress]               = useState(0);
    const rafRef = useRef<number | null>(null);

    // Memoised — only recalculates when hovered/active change
    const displayed       = useMemo(() => hovered !== null ? hovered : active, [hovered, active]);
    const indicatorTarget = useMemo(() => hovered !== null ? hovered : active, [hovered, active]);
    const current         = useMemo(() => projects[displayed], [displayed]);

    useEffect(() => {
        const raf = requestAnimationFrame(() => {
            const el   = itemRefs.current[indicatorTarget];
            const list = listRef.current;
            if (el && list) {
                const er = el.getBoundingClientRect();
                const lr = list.getBoundingClientRect();
                setIndicatorTop(er.top - lr.top);
                setIndicatorHeight(er.height);
            }
        });
        return () => cancelAnimationFrame(raf);
    }, [indicatorTarget]);

    const startProgress = useCallback(() => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        startRef.current = Date.now();
        const tick = () => {
            const elapsed = Date.now() - startRef.current;
            const p = Math.min(elapsed / AUTO_INTERVAL, 1);
            setProgress(p);
            if (p < 1) rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
    }, []); // no deps — only uses refs and constants

    const resetAutoPlay = useCallback(() => {
        if (autoRef.current) clearInterval(autoRef.current);
        setProgress(0);
        startProgress();
        autoRef.current = setInterval(() => {
            setActive(prev => (prev + 1) % PROJECTS_LEN);
        }, AUTO_INTERVAL);
    }, [startProgress]);

    useEffect(() => {
        resetAutoPlay();
        return () => {
            if (autoRef.current) clearInterval(autoRef.current);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [resetAutoPlay]);

    useEffect(() => {
        startProgress();
    }, [active, startProgress]);

    const handleClick = useCallback((i: number) => {
        if (i === active) return;
        setAnimating(true);
        setTimeout(() => { setActive(i); setAnimating(false); }, 220);
        resetAutoPlay();
    }, [active, resetAutoPlay]);

    const handleMouseEnter = useCallback((i: number) => setHovered(i),  []);
    const handleMouseLeave = useCallback(()           => setHovered(null), []);

    // Memoised per-item state — recalculates only when hovered/active change
    const itemStates = useMemo(() => projects.map((_, i) => {
        if (hovered !== null) return hovered === i ? "hovered" : "dimmed";
        return active === i ? "active" : "dimmed";
    }), [hovered, active]);

    // Memoised indicator fill style — only changes when hovered/progress change
    const indicatorFillStyle = useMemo(() => ({
        top: hovered !== null ? "0%" : `${progress * 100}%`,
        background: 'linear-gradient(to bottom, #9333ea, #2563eb)',
        boxShadow: '0 0 8px rgba(147,51,234,0.7)',
        transition: hovered !== null ? 'top 0.2s ease' : 'none',
    }), [hovered, progress]);

    // Memoised indicator track style — changes on indicatorTop/indicatorHeight
    const indicatorTrackStyle = useMemo(() => ({
        top:        indicatorTop + 6,
        height:     indicatorHeight > 12 ? indicatorHeight - 12 : indicatorHeight,
        background: 'rgba(255,255,255,0.08)',
        transition: 'top 0.35s cubic-bezier(0.25,1,0.3,1), height 0.35s cubic-bezier(0.25,1,0.3,1)',
    }), [indicatorTop, indicatorHeight]);

    return (
        <div ref={sectionRef} className="w-full py-20 md:py-24 relative z-10 overflow-hidden" id="projects">
            <style>{`
                @keyframes textSlide {
                    from { opacity: 0; transform: translateY(10px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .text-slide { animation: textSlide 0.35s cubic-bezier(0.4,0,0.2,1) 0.08s both; }
                .proj-item { position: relative; cursor: pointer; transition: all 0.45s cubic-bezier(0.25,1,0.3,1); }
            `}</style>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8">

                {/* Header */}
                <div className="text-center mb-8 md:mb-16">
                    <div className="inline-flex items-center gap-2.5 px-5 py-2.5 mb-7 rounded-full border border-white/10 bg-white/[0.04]">
                        <Briefcase className="w-4 h-4 text-cyan-400" />
                        <span className="text-xs font-semibold text-gray-300 tracking-widest uppercase">Our Portfolio</span>
                    </div>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
                        Latest{" "}
                        <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                            Projects
                        </span>
                    </h2>
                    <p className="text-base md:text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed">
                        Build vital capabilities to deliver digital outcomes. Explore our latest work.
                    </p>
                </div>

                {/* Split layout */}
                <div className="grid lg:grid-cols-[5fr_7fr] gap-6 lg:gap-16 items-center">

                    {/* LEFT — project list */}
                    <div className="flex flex-col gap-0.5">
                        <div ref={listRef} className="relative flex flex-col gap-0.5">

                            {/* Sliding indicator bar */}
                            <div
                                className="absolute left-0 w-[2.5px] rounded-full pointer-events-none overflow-hidden"
                                style={indicatorTrackStyle}
                            >
                                <div className="absolute left-0 right-0 bottom-0 rounded-full" style={indicatorFillStyle} />
                            </div>

                            {projects.map((p, i) => {
                                const state = itemStates[i];
                                const Icon  = p.icon;

                                const isHighlighted = state === "active" || state === "hovered";

                                const titleColor =
                                    state === "hovered" ? "white" :
                                    state === "active"  ? "white" :
                                    "rgba(255,255,255,0.25)";

                                const iconBg      = isHighlighted ? 'linear-gradient(135deg, #9333ea, #2563eb)' : 'linear-gradient(135deg, rgba(147,51,234,0.25), rgba(37,99,235,0.25))';
                                const iconOpacity = isHighlighted ? 1 : 0.35;
                                const iconShadow  = isHighlighted ? '0 0 16px rgba(147,51,234,0.45)' : 'none';

                                return (
                                    <button
                                        key={p.slug}
                                        ref={el => { itemRefs.current[i] = el; }}
                                        onClick={() => handleClick(i)}
                                        onMouseEnter={() => handleMouseEnter(i)}
                                        onMouseLeave={handleMouseLeave}
                                        className="proj-item text-left pl-5 py-3 pr-3"
                                        style={{
                                            transform:  state === "hovered" ? "translateX(4px)" : "translateX(0px)",
                                            transition: "transform 0.25s cubic-bezier(0.25,1,0.3,1)",
                                        }}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div
                                                className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-300"
                                                style={{ background: iconBg, opacity: iconOpacity, boxShadow: iconShadow }}
                                            >
                                                <Icon className="w-5 h-5 text-white" />
                                            </div>
                                            <span
                                                className="text-base md:text-lg font-bold leading-tight"
                                                style={{ color: titleColor, transition: "color 0.2s ease" }}
                                            >
                                                {p.title}
                                            </span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* RIGHT — image + description */}
                    <div className="relative">
                        <div
                            className="pointer-events-none absolute -inset-10 rounded-3xl blur-[80px] opacity-30"
                            style={{ background: "radial-gradient(ellipse, rgba(147,51,234,0.25) 0%, transparent 70%)" }}
                        />
                        <div className="relative">
                            <div
                                className="relative aspect-[16/10] w-full rounded-[18px] overflow-hidden border border-white/[0.08]"
                                style={{ boxShadow: "0 28px 90px rgba(0,0,0,0.55), 0 0 0 1px rgba(147,51,234,0.12)" }}
                            >
                                {projects.map((p, i) => (
                                    <img
                                        key={p.slug}
                                        src={p.img}
                                        alt={p.title}
                                        className="absolute inset-0 w-full h-full object-cover"
                                        style={{
                                            opacity:    displayed === i && !animating ? 1 : 0,
                                            transform:  displayed === i && !animating ? "scale(1)" : "scale(1.03)",
                                            transition: "opacity 0.3s ease, transform 0.4s cubic-bezier(0.4,0,0.2,1)",
                                            zIndex:     displayed === i ? 1 : 0,
                                        }}
                                    />
                                ))}

                                <div
                                    className="pointer-events-none absolute inset-0 rounded-[18px]"
                                    style={{
                                        background: "linear-gradient(to bottom, transparent 25%, rgba(4,4,18,0.55) 58%, rgba(4,4,18,0.92) 100%)",
                                        zIndex: 2,
                                    }}
                                />

                                {!animating && (
                                    <div
                                        key={`card-${displayed}`}
                                        className="text-slide absolute bottom-0 left-0 right-0 rounded-b-[18px] px-6 pb-6 pt-8"
                                        style={{
                                            background: "linear-gradient(to top, rgba(4,4,18,0.88) 60%, transparent 100%)",
                                            backdropFilter: "blur(2px)",
                                            zIndex: 3,
                                        }}
                                    >
                                        <div className="flex items-end justify-between gap-4">
                                            <div>
                                                <p className="font-bold text-white md:text-lg drop-shadow-lg mb-1">{current.title}</p>
                                                <p className="text-sm leading-relaxed max-w-md text-white/70" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}>
                                                    {current.description}
                                                </p>
                                            </div>
                                            <a
                                                href={`/services/${current.slug}`}
                                                className="shrink-0 inline-flex items-center gap-1.5 text-sm font-medium border-b pb-px transition-all duration-200 hover:gap-2.5 whitespace-nowrap"
                                                style={{ color: 'rgba(167,139,250,0.9)', borderColor: 'rgba(167,139,250,0.3)' }}
                                            >
                                                View
                                                <ArrowUpRight className="w-3.5 h-3.5" />
                                            </a>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}